import type { OnMount } from "@monaco-editor/react";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ReactNode } from "react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import { ArrowLeft, BookOpenText, Download, FileCode2, ListChecks, PackagePlus, Play, Save, Upload } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardSkeleton } from "@/components/shared/loading-skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ROUTES } from "@/app/routes";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { cn } from "@/lib/utils/cn";
import { toastMutationError } from "@/lib/utils/formatting";
import {
  getApiConnectorPackagesConnectorPackageIdExport,
  getGetApiConnectorPackagesConnectorPackageIdQueryKey,
  getGetApiConnectorPackagesQueryKey,
  useGetApiConnectorPackages,
  useGetApiConnectorPackagesConnectorPackageId,
  useGetApiConnectorPackagesConnectorPackageIdEditorLanguage,
  usePostApiConnectorPackagesConnectorPackageIdValidate,
  usePostApiConnectorPackagesFromTemplate,
  usePostApiConnectorPackagesImport,
  usePutApiConnectorPackagesConnectorPackageIdFiles,
} from "@/lib/api/generated/connector-packages/connector-packages";
import type {
  ConnectorPackageDiagnosticDto,
  ConnectorPackageDto,
  ConnectorPackageExportDto,
  ConnectorPackageFileDto,
} from "@/lib/api/generated/control-api.schemas";
import { GeneratedArtifactsPanel, PublishPanel, ValidationPanel } from "./connector-package-panels";

const MonacoEditor = lazy(() => import("@monaco-editor/react"));

const createPackageSchema = z.object({
  connectorKey: z.string().min(1).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase words separated by hyphens."),
  displayName: z.string().min(1),
  description: z.string().optional(),
  initialToolNames: z.string().min(1),
  configFields: z.string().optional(),
  secretNames: z.string().optional(),
  firstToolExecutionMode: z.enum(["sync", "background"]),
});

type CreatePackageForm = z.infer<typeof createPackageSchema>;

type ValidationState = {
  packageId: string;
  diagnostics: ConnectorPackageDiagnosticDto[];
  ok: boolean;
} | null;

type MonacoEditorInstance = Parameters<OnMount>[0];
type MonacoApi = Parameters<OnMount>[1];
type MonacoDisposable = { dispose(): void };

type FileGuideAction = {
  label: string;
  description: string;
  content: string;
};

type FileGuideDefinition = {
  title: string;
  body: string[];
  sections?: Array<{
    title: string;
    lines: string[];
  }>;
  example: string;
  actions?: FileGuideAction[];
};

export function ConnectorPackageListPage() {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: packages = [], isLoading } = useGetApiConnectorPackages();
  const importPackage = usePostApiConnectorPackagesImport();
  const [importOpen, setImportOpen] = useState(false);
  const [importJson, setImportJson] = useState("");
  const [connectorKeyOverride, setConnectorKeyOverride] = useState("");
  const [displayNameOverride, setDisplayNameOverride] = useState("");
  const sortedPackages = useMemo(
    () => [...packages].toSorted((left, right) => left.connectorKey.localeCompare(right.connectorKey)),
    [packages],
  );

  const importConnector = () => {
    let parsed: ConnectorPackageExportDto;
    try {
      parsed = JSON.parse(importJson) as ConnectorPackageExportDto;
    } catch {
      toast.error("Import file must be valid connector package JSON");
      return;
    }
    importPackage.mutate(
      {
        data: {
          package: parsed,
          connectorKeyOverride: connectorKeyOverride.trim() || null,
          displayNameOverride: displayNameOverride.trim() || null,
        },
      },
      {
        onSuccess: (created) => {
          toast.success("Connector imported");
          setImportOpen(false);
          setImportJson("");
          setConnectorKeyOverride("");
          setDisplayNameOverride("");
          void queryClient.invalidateQueries({ queryKey: getGetApiConnectorPackagesQueryKey() });
          navigate(ROUTES.tenantConnector(tenantId, created.connectorPackageId));
        },
        onError: (error) => toast.error(toastMutationError(error, "Failed to import connector")),
      },
    );
  };

  const loadImportFile = (file?: File) => {
    if (!file) return;
    void file.text().then(setImportJson).catch(() => toast.error("Could not read import file"));
  };

  if (isLoading) {
    return <CardSkeleton />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: "var(--text-strong)" }}>
            Connectors
          </h1>
          <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
            Platform-owned connector packages.
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="size-4" />
            Import Connector
          </Button>
          <Button type="button" onClick={() => navigate(ROUTES.tenantConnectorNew(tenantId))}>
            <PackagePlus className="size-4" />
            New Connector
          </Button>
        </div>
      </div>

      {sortedPackages.length === 0 ? (
        <EmptyState
          title="No connectors"
          description="Create a connector package from the static TypeScript template."
          action={{
            label: "New Connector",
            onClick: () => navigate(ROUTES.tenantConnectorNew(tenantId)),
          }}
        />
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Connector</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Draft</TableHead>
                <TableHead>Published</TableHead>
                <TableHead className="text-right">Tools</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedPackages.map((item) => (
                <TableRow
                  key={item.connectorPackageId}
                  className="cursor-pointer"
                  onClick={() => navigate(ROUTES.tenantConnector(tenantId, item.connectorPackageId))}
                >
                  <TableCell className="font-medium">{item.displayName}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{item.connectorKey}</TableCell>
                  <TableCell>
                    <Badge variant={item.status === "published" ? "active" : "draft"}>{item.status}</Badge>
                  </TableCell>
                  <TableCell>v{item.draftVersion}</TableCell>
                  <TableCell>{item.publishedVersion ? `v${item.publishedVersion}` : "none"}</TableCell>
                  <TableCell className="text-right">{item.tools.length}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Connector</DialogTitle>
            <DialogDescription>
              Import a connector package export as a new draft. Secrets, activations, published versions, and runtime state are not imported.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Export JSON File</Label>
              <Input type="file" accept="application/json,.json" onChange={(event) => loadImportFile(event.target.files?.[0])} />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Connector Key Override</Label>
                <Input
                  placeholder="required when key already exists"
                  value={connectorKeyOverride}
                  onChange={(event) => setConnectorKeyOverride(event.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label>Display Name Override</Label>
                <Input
                  placeholder="optional"
                  value={displayNameOverride}
                  onChange={(event) => setDisplayNameOverride(event.target.value)}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Export JSON</Label>
              <Textarea
                className="font-mono text-xs"
                rows={14}
                value={importJson}
                onChange={(event) => setImportJson(event.target.value)}
                placeholder='{"schemaVersion":"connector_package_export_v1",...}'
              />
            </div>
          </div>
          <DialogFooter showCloseButton>
            <Button type="button" onClick={importConnector} disabled={importPackage.isPending || !importJson.trim()}>
              <Upload className="size-4" />
              Import Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ConnectorPackageNewPage() {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createPackage = usePostApiConnectorPackagesFromTemplate();
  const form = useForm<CreatePackageForm>({
    resolver: zodResolver(createPackageSchema),
    defaultValues: {
      connectorKey: "",
      displayName: "",
      description: "",
      initialToolNames: "example_start",
      configFields: "",
      secretNames: "",
      firstToolExecutionMode: "sync",
    },
  });

  const createFromTemplate = form.handleSubmit((values) => {
    createPackage.mutate(
      {
        data: {
          connectorKey: values.connectorKey,
          displayName: values.displayName,
          description: values.description || null,
          initialToolNames: splitLines(values.initialToolNames),
          configFields: splitLines(values.configFields).map((key) => ({
            key,
            type: key.toLowerCase().includes("url") ? "url" : "string",
            label: labelize(key),
            defaultValue: null,
          })),
          secretNames: splitLines(values.secretNames),
          firstToolExecutionMode: values.firstToolExecutionMode,
        },
      },
      {
        onSuccess: (created) => {
          toast.success("Connector created");
          void queryClient.invalidateQueries({ queryKey: getGetApiConnectorPackagesQueryKey() });
          navigate(ROUTES.tenantConnector(tenantId, created.connectorPackageId));
        },
        onError: (error) => toast.error(toastMutationError(error, "Failed to create connector")),
      },
    );
  });

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: "var(--text-strong)" }}>
            New Connector
          </h1>
          <p className="text-sm text-muted-foreground">Create the static TypeScript source package.</p>
        </div>
        <Button type="button" variant="outline" onClick={() => navigate(ROUTES.tenantConnectors(tenantId))}>
          <ArrowLeft className="size-4" />
          Back
        </Button>
      </div>

      <form className="grid gap-4 rounded-md border border-border bg-card p-4 md:grid-cols-2" onSubmit={createFromTemplate}>
        <FormField label="Connector Key" error={form.formState.errors.connectorKey?.message}>
          <Input placeholder="insurance-workbench" {...form.register("connectorKey")} />
        </FormField>
        <FormField label="Display Name" error={form.formState.errors.displayName?.message}>
          <Input placeholder="Insurance Workbench" {...form.register("displayName")} />
        </FormField>
        <FormField label="Initial Tool Names" error={form.formState.errors.initialToolNames?.message}>
          <Textarea rows={3} placeholder="compare_start" {...form.register("initialToolNames")} />
        </FormField>
        <FormField label="Secret Names" error={form.formState.errors.secretNames?.message}>
          <Textarea rows={3} placeholder="serviceToken" {...form.register("secretNames")} />
        </FormField>
        <FormField label="Config Fields" error={form.formState.errors.configFields?.message}>
          <Textarea rows={3} placeholder="baseUrl" {...form.register("configFields")} />
        </FormField>
        <Controller
          control={form.control}
          name="firstToolExecutionMode"
          render={({ field }) => (
            <FormField label="First Tool Execution">
              <div className="flex gap-2">
                {(["sync", "background"] as const).map((value) => (
                  <Button
                    key={value}
                    type="button"
                    variant={field.value === value ? "default" : "outline"}
                    onClick={() => field.onChange(value)}
                  >
                    {value}
                  </Button>
                ))}
              </div>
            </FormField>
          )}
        />
        <FormField label="Description" className="md:col-span-2" error={form.formState.errors.description?.message}>
          <Textarea rows={3} placeholder="What this connector does." {...form.register("description")} />
        </FormField>
        <div className="flex justify-end md:col-span-2">
          <Button type="submit" disabled={createPackage.isPending}>
            <PackagePlus className="size-4" />
            Create Connector
          </Button>
        </div>
      </form>
    </div>
  );
}

export function ConnectorPackageWorkbenchPage() {
  const { tenantId } = useTenantContext();
  const { connectorPackageId } = useParams<{ connectorPackageId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: packages = [], isLoading } = useGetApiConnectorPackages();
  const packageDetail = useGetApiConnectorPackagesConnectorPackageId(connectorPackageId ?? "", {
    query: { enabled: Boolean(connectorPackageId) },
  });
  const saveFile = usePutApiConnectorPackagesConnectorPackageIdFiles();
  const validatePackage = usePostApiConnectorPackagesConnectorPackageIdValidate();
  const [exporting, setExporting] = useState(false);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [draftContent, setDraftContent] = useState<string | null>(null);
  const [validationState, setValidationState] = useState<ValidationState>(null);
  const [guideOpen, setGuideOpen] = useState(true);
  const editorRef = useRef<MonacoEditorInstance | null>(null);
  const monacoRef = useRef<MonacoApi | null>(null);
  const editorTypeLibraryDisposablesRef = useRef<MonacoDisposable[]>([]);

  const selectedPackage = packageDetail.data ?? packages.find((item) => item.connectorPackageId === connectorPackageId);
  const editorLanguage = useGetApiConnectorPackagesConnectorPackageIdEditorLanguage(selectedPackage?.connectorPackageId ?? "", {
    query: { enabled: Boolean(selectedPackage?.connectorPackageId) },
  });
  const sortedFiles = useMemo(
    () => [...(selectedPackage?.files ?? [])].toSorted((left, right) => left.relativePath.localeCompare(right.relativePath)),
    [selectedPackage?.files],
  );
  const selectedFile =
    sortedFiles.find((file) => file.relativePath === selectedPath) ??
    sortedFiles.find((file) => file.relativePath === "src/connector.ts") ??
    sortedFiles[0];
  const editorValue = draftContent ?? selectedFile?.content ?? "";
  const activeValidation =
    validationState?.packageId === selectedPackage?.connectorPackageId ? validationState : null;
  const guide = selectedFile ? guideForFile(selectedFile.relativePath) : null;

  const selectFile = (file: ConnectorPackageFileDto) => {
    setSelectedPath(file.relativePath);
    setDraftContent(null);
  };

  const syncEditorTypeLibraries = (monaco: MonacoApi) => {
    for (const disposable of editorTypeLibraryDisposablesRef.current) {
      disposable.dispose();
    }
    editorTypeLibraryDisposablesRef.current = [];

    for (const typeDefinition of editorLanguage.data?.typeDefinitions ?? []) {
      editorTypeLibraryDisposablesRef.current.push(
        monaco.languages.typescript.typescriptDefaults.addExtraLib(
          typeDefinition.content,
          `file:///${typeDefinition.path}`,
        ),
      );
    }
    for (const file of selectedPackage?.files ?? []) {
      if (!file.relativePath.endsWith(".ts")) continue;
      editorTypeLibraryDisposablesRef.current.push(
        monaco.languages.typescript.typescriptDefaults.addExtraLib(
          file.content,
          `file:///${selectedPackage.connectorPackageId}/${file.relativePath}`,
        ),
      );
    }
  };

  useEffect(() => {
    const monaco = monacoRef.current;
    if (!monaco) return;
    syncEditorTypeLibraries(monaco);

    return () => {
      for (const disposable of editorTypeLibraryDisposablesRef.current) {
        disposable.dispose();
      }
      editorTypeLibraryDisposablesRef.current = [];
    };
  }, [editorLanguage.data?.typeDefinitions, selectedPackage?.connectorPackageId, selectedPackage?.files]);

  const handleEditorMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      allowNonTsExtensions: true,
      allowSyntheticDefaultImports: true,
      downlevelIteration: true,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      noEmit: true,
      strict: true,
      target: monaco.languages.typescript.ScriptTarget.ES2022,
    });
    syncEditorTypeLibraries(monaco);
    editor.focus();
  };

  const checkActiveFile = () => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    const model = editor?.getModel();
    if (!editor || !monaco || !model) return;

    const markers = monaco.editor
      .getModelMarkers({ resource: model.uri })
      .filter((marker) => marker.severity >= monaco.MarkerSeverity.Warning);
    if (markers.length === 0) {
      toast.success("No file diagnostics");
      return;
    }

    const firstMarker = markers[0];
    editor.revealLineInCenter(firstMarker.startLineNumber);
    editor.setPosition({
      lineNumber: firstMarker.startLineNumber,
      column: firstMarker.startColumn,
    });
    const summary = markers
      .slice(0, 3)
      .map((marker) => `L${marker.startLineNumber}: ${marker.message}`)
      .join("\n");
    toast.error(`${markers.length} file diagnostic(s)\n${summary}`);
  };

  const applyGuideTemplate = (content: string) => {
    if (!selectedFile) return;
    setDraftContent(content === selectedFile.content ? null : content);
    toast.success("Template applied");
  };

  const saveActiveFile = () => {
    if (!selectedPackage || !selectedFile) return;
    saveFile.mutate(
      {
        connectorPackageId: selectedPackage.connectorPackageId,
        data: {
          relativePath: selectedFile.relativePath,
          content: editorValue,
          contentType: selectedFile.contentType,
        },
      },
      {
        onSuccess: () => {
          toast.success("File saved");
          setDraftContent(null);
          void queryClient.invalidateQueries({ queryKey: getGetApiConnectorPackagesQueryKey() });
          void queryClient.invalidateQueries({
            queryKey: getGetApiConnectorPackagesConnectorPackageIdQueryKey(selectedPackage.connectorPackageId),
          });
        },
        onError: (error) => toast.error(toastMutationError(error, "Failed to save file")),
      },
    );
  };

  const validateActivePackage = () => {
    if (!selectedPackage) return;
    validatePackage.mutate(
      { connectorPackageId: selectedPackage.connectorPackageId },
      {
        onSuccess: (result) => {
          setValidationState({
            packageId: selectedPackage.connectorPackageId,
            diagnostics: result.diagnostics,
            ok: result.ok,
          });
          toast[result.ok ? "success" : "error"](result.ok ? "Validation passed" : "Validation failed");
        },
        onError: (error) => toast.error(toastMutationError(error, "Failed to validate connector")),
      },
    );
  };

  const exportActivePackage = async () => {
    if (!selectedPackage) return;
    setExporting(true);
    try {
      const exported = await getApiConnectorPackagesConnectorPackageIdExport(selectedPackage.connectorPackageId);
      downloadJson(
        `${selectedPackage.connectorKey}-connector-package.json`,
        exported,
      );
      toast.success("Connector exported");
    } catch (error) {
      toast.error(toastMutationError(error, "Failed to export connector"));
    } finally {
      setExporting(false);
    }
  };

  if (isLoading || packageDetail.isLoading) {
    return <CardSkeleton />;
  }

  if (!selectedPackage) {
    return (
      <EmptyState
        title="Connector not found"
        description="Return to the connector list and choose an existing connector."
        action={{ label: "Open Connectors", onClick: () => navigate(ROUTES.tenantConnectors(tenantId)) }}
      />
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-7rem)] flex-1 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Button type="button" variant="ghost" className="mb-2 px-0" onClick={() => navigate(ROUTES.tenantConnectors(tenantId))}>
            <ArrowLeft className="size-4" />
            Connectors
          </Button>
          <h1 className="truncate text-xl font-semibold" style={{ color: "var(--text-strong)" }}>
            {selectedPackage.displayName}
          </h1>
          <p className="text-sm text-muted-foreground">
            {selectedPackage.connectorKey} · Draft v{selectedPackage.draftVersion} · SDK {selectedPackage.sdkVersion}
          </p>
          {editorLanguage.data ? (
            <p className="text-xs text-muted-foreground">
              Editor SDK {editorLanguage.data.sdkVersion} · {editorLanguage.data.typeDefinitions.length} type lib
              {editorLanguage.data.diagnostics.length > 0 ? ` · ${editorLanguage.data.diagnostics.length} diagnostic(s)` : ""}
            </p>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={() => void exportActivePackage()} disabled={exporting}>
            <Download className="size-4" />
            Export
          </Button>
          <Button type="button" variant="outline" onClick={validateActivePackage} disabled={validatePackage.isPending}>
            <Play className="size-4" />
            Validate
          </Button>
          <Button type="button" onClick={saveActiveFile} disabled={!selectedFile || saveFile.isPending || draftContent === null}>
            <Save className="size-4" />
            Save File
          </Button>
        </div>
      </div>

      <Tabs defaultValue="source" className="flex min-h-0 flex-1 flex-col gap-4">
        <TabsList className="shrink-0">
          <TabsTrigger value="source">Source</TabsTrigger>
          <TabsTrigger value="validation">Validation</TabsTrigger>
          <TabsTrigger value="generated">Generated</TabsTrigger>
          <TabsTrigger value="publish">Publish</TabsTrigger>
        </TabsList>

        <TabsContent value="source" className="min-h-0 flex-1">
          <div className="grid h-full min-h-[620px] grid-cols-[240px_minmax(0,1fr)] overflow-hidden rounded-md border border-border bg-card">
            <div className="min-h-0 border-r border-border">
              <div className="border-b border-border p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Files</p>
              </div>
              <div className="p-2">
                {sortedFiles.map((file) => (
                  <button
                    key={file.relativePath}
                    type="button"
                    onClick={() => selectFile(file)}
                    className={cn(
                      "mb-1 flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted",
                      file.relativePath === selectedFile?.relativePath && "bg-muted",
                    )}
                  >
                    <FileCode2 className="size-4 shrink-0" />
                    <span className="truncate">{file.relativePath}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className={cn("grid min-h-0", guideOpen && guide ? "grid-cols-[minmax(0,1fr)_320px]" : "grid-cols-1")}>
              <div className="flex min-h-0 flex-col">
                <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{selectedFile?.relativePath ?? "No file selected"}</p>
                    {draftContent !== null ? <Badge variant="secondary">Unsaved</Badge> : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={checkActiveFile} disabled={!selectedFile}>
                      <ListChecks className="size-4" />
                      Check File
                    </Button>
                    {guide ? (
                      <Button type="button" variant="outline" size="sm" onClick={() => setGuideOpen((open) => !open)}>
                        <BookOpenText className="size-4" />
                        Guide
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div className="min-h-0 flex-1">
                  <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Loading editor...</div>}>
                    <MonacoEditor
                      height="100%"
                      language={selectedFile?.relativePath.endsWith(".md") ? "markdown" : "typescript"}
                      path={`file:///${selectedPackage.connectorPackageId}/${selectedFile?.relativePath ?? "empty"}`}
                      value={editorValue}
                      onChange={(value) => {
                        const nextValue = value ?? "";
                        setDraftContent(nextValue === (selectedFile?.content ?? "") ? null : nextValue);
                      }}
                      onMount={handleEditorMount}
                      options={{
                        minimap: { enabled: false },
                        fontSize: 13,
                        scrollBeyondLastLine: false,
                        wordWrap: "on",
                      }}
                    />
                  </Suspense>
                </div>
              </div>

              {guideOpen && guide ? <FileGuide guide={guide} onApplyTemplate={applyGuideTemplate} /> : null}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="validation">
          <ValidationPanel state={activeValidation} />
        </TabsContent>
        <TabsContent value="generated">
          <GeneratedArtifactsPanel selectedPackage={selectedPackage} />
        </TabsContent>
        <TabsContent value="publish">
          <PublishPanel selectedPackage={selectedPackage} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FileGuide({
  guide,
  onApplyTemplate,
}: {
  guide: FileGuideDefinition;
  onApplyTemplate: (content: string) => void;
}) {
  return (
    <aside className="min-h-0 overflow-auto border-l border-border bg-muted/30 p-4">
      <div className="flex items-center gap-2">
        <BookOpenText className="size-4" />
        <h2 className="text-sm font-semibold">{guide.title}</h2>
      </div>
      <div className="mt-4 grid gap-3 text-sm text-muted-foreground">
        {guide.body.map((line, index) => (
          <p key={index}>{line}</p>
        ))}
      </div>
      {guide.sections ? (
        <div className="mt-5 grid gap-4">
          {guide.sections.map((section) => (
            <section key={section.title} className="grid gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{section.title}</h3>
              <ul className="grid gap-1 text-sm text-muted-foreground">
                {section.lines.map((line, index) => (
                  <li key={index}>{line}</li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : null}
      {guide.actions ? (
        <div className="mt-5 grid gap-2">
          {guide.actions.map((action) => (
            <Button
              key={action.label}
              type="button"
              variant="outline"
              className="h-auto justify-start px-3 py-2 text-left"
              onClick={() => onApplyTemplate(action.content)}
            >
              <span className="grid gap-1">
                <span className="text-sm font-medium">{action.label}</span>
                <span className="text-xs font-normal text-muted-foreground">{action.description}</span>
              </span>
            </Button>
          ))}
        </div>
      ) : null}
      <pre className="mt-4 overflow-auto rounded-md bg-background p-3 text-xs">
        <code>{guide.example}</code>
      </pre>
    </aside>
  );
}

const CONNECTOR_HOOKS_TEMPLATE = `import { defineConnector, defineConnectorOperation } from "@openclaw/connectors";
import type { ExampleStartInput, ExampleStartOutput } from "./schemas";

export const connector = defineConnector({
  key: "example-connector",
  name: "Example Connector",
  description: "Describe the integration.",
  hooks: {
    beforeAny: [
      async ({ request }) => {
        if (!request.tenantId) {
          throw new Error("tenantId is required.");
        }
      },
    ],
    afterAny: [
      async ({ request, output }) => {
        void request;
        void output;
      },
    ],
  },
  capabilities: {
    "example.start": {
      name: "Example start",
      description: "Describe when the agent should use this operation.",
      operations: {
        start: defineConnectorOperation<ExampleStartInput, ExampleStartOutput>({
          operationType: "read",
          async run({ input, request }) {
            return {
              ok: true,
              actionKey: "example_start",
              requestedAt: request.requestedAt,
              requestId: input.requestId,
            };
          },
        }),
      },
    },
  },
});

export const CONNECTOR = connector.connector;
export const PROVIDER_DESCRIPTOR = connector.providerDescriptor;

export function createCapabilityProvider() {
  return connector.createCapabilityProvider();
}
`;

function guideForFile(path: string) {
  if (path === "src/index.ts") {
    return {
      title: "Connector entrypoint",
      body: [
        "Keep this file as the package entrypoint.",
        "Export the connector and createCapabilityProvider from src/connector.ts.",
      ],
      example: `export {\n  CONNECTOR,\n  PROVIDER_DESCRIPTOR,\n  connector,\n  createCapabilityProvider,\n} from "./connector";`,
    };
  }
  if (path === "src/connector.ts") {
    return {
      title: "Connector definition",
      body: [
        "Define metadata, capabilities, operations, lifecycle hooks, and external calls here.",
        "Use operationType values from ProviderDescriptorOperationType: read, prepare, submit, verify.",
        "Use request.backgroundTasks for long-running work. The runtime invokes createCapabilityProvider().",
      ],
      sections: [
        {
          title: "run input",
          lines: [
            "run receives { input, request, context, client }.",
            "Use defineConnectorOperation<Input, Output>() when a tool needs exact input and output IntelliSense.",
            "request includes tenantId, capabilityKey, operationKey, providerKey, payload, actor, requestedAt, executionClass, binding, connectorInstall, connectorConfig, connectorCredentials, snapshot, channel, conversationId, agentId, workflowKey, and backgroundTasks.",
            "Return a plain object matching the operation output type. Throw an Error with code/message fields for structured failures.",
          ],
        },
        {
          title: "hooks",
          lines: [
            "Use before on an operation for operation-specific validation.",
            "Use after on an operation for operation-specific audit after success.",
            "Use onError on an operation for operation-specific failure telemetry.",
            "Use beforeAny, afterAny, and onErrorAny only for cross-operation behavior.",
          ],
        },
      ],
      actions: [
        {
          label: "Insert lifecycle example",
          description: "Shows beforeAny, afterAny, and one operation in the canonical file.",
          content: CONNECTOR_HOOKS_TEMPLATE,
        },
      ],
      example: `start: defineConnectorOperation<CompareStartInput, CompareStartOutput>({\n  operationType: "read",\n  async run({ input, request, client }) {\n    return { caseId: request.correlationId ?? "case", status: "in_progress" };\n  },\n})`,
    };
  }
  if (path === "src/schemas.ts") {
    return {
      title: "Types and schemas",
      body: [
        "Define operation input/output schemas here once with ConnectorSchema.",
        "Derive TypeScript types from those schemas with InferConnectorSchema.",
        "Do not duplicate the same fields as both hand-written JSON schema in src/connector.ts and hand-written TypeScript types here.",
      ],
      example: `import { ConnectorSchema, type InferConnectorSchema } from "@openclaw/connectors";\n\nexport const compareStartInputSchema = ConnectorSchema.Object(\n  {\n    fileIds: ConnectorSchema.Optional(ConnectorSchema.Array(ConnectorSchema.String())),\n  },\n  { additionalProperties: false },\n);\n\nexport type CompareStartInput = InferConnectorSchema<typeof compareStartInputSchema>;`,
    };
  }
  if (path.endsWith(".ts")) {
    return {
      title: "TypeScript source",
      body: [
        "Use package-local imports and @openclaw/connectors only.",
        "Keep logic inside the fixed source structure: src/connector.ts, src/schemas.ts, and src/index.ts.",
      ],
      example: `import { defineConnector } from "@openclaw/connectors";`,
    };
  }
  if (path === "README.md") {
    return {
      title: "Connector notes",
      body: ["Explain operational intent, required configuration, secrets, and expected tool behavior."],
      example: `# Insurance Workbench\n\nCompares selected insurance documents through the approved analysis service.`,
    };
  }
  return null;
}

function FormField({
  label,
  error,
  className,
  children,
}: {
  label: string;
  error?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("grid gap-2", className)}>
      <Label>{label}</Label>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function splitLines(value?: string | null): string[] {
  return (value ?? "")
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function labelize(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function downloadJson(fileName: string, value: unknown) {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
